import type { SubtitleStyle } from '../types/videoEdit';

export type SubtitleAppearancePresetId =
  | 'white_black'
  | 'white_gray'
  | 'yellow_black'
  | 'yellow_red'
  | 'pink_red'
  | 'blue_black'
  | 'blue_white'
  | 'cyan_black'
  | 'green_black'
  | 'cream_brown'
  | 'peach_brown'
  | 'pink_black'
  | 'black_white'
  | 'mint_black';

interface SubtitleAppearancePresetConfig {
  id: SubtitleAppearancePresetId;
  textColor: string;
  outlineColor: string;
  outlineScale: number;
  minOutline: number;
  shadow: number;
  cssOutlineOpacity: number;
  cssShadowOpacity: number;
  cssShadowBlur: number;
  previewSurface: string;
}

const PRESET_CONFIGS: Record<SubtitleAppearancePresetId, SubtitleAppearancePresetConfig> = {
  white_black: {
    id: 'white_black',
    textColor: '#FFFFFF',
    outlineColor: '#000000',
    outlineScale: 0.025,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.35,
    cssShadowBlur: 3,
    previewSurface: '#2E2E31',
  },
  white_gray: {
    id: 'white_gray',
    textColor: '#FFFFFF',
    outlineColor: '#7A7D86',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.7,
    cssShadowOpacity: 0.22,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  yellow_black: {
    id: 'yellow_black',
    textColor: '#FFE329',
    outlineColor: '#111111',
    outlineScale: 0.025,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.74,
    cssShadowOpacity: 0.26,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  yellow_red: {
    id: 'yellow_red',
    textColor: '#FFD91A',
    outlineColor: '#D93A2F',
    outlineScale: 0.025,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.76,
    cssShadowOpacity: 0.22,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  pink_red: {
    id: 'pink_red',
    textColor: '#FFD6D6',
    outlineColor: '#FF6B6B',
    outlineScale: 0.025,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.78,
    cssShadowOpacity: 0.22,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  blue_black: {
    id: 'blue_black',
    textColor: '#AED9FF',
    outlineColor: '#1A1A1A',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.22,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  blue_white: {
    id: 'blue_white',
    textColor: '#4EA0FF',
    outlineColor: '#FFFFFF',
    outlineScale: 0.024,
    minOutline: 1,
    shadow: 0,
    cssOutlineOpacity: 0.95,
    cssShadowOpacity: 0,
    cssShadowBlur: 0,
    previewSurface: '#2E2E31',
  },
  cyan_black: {
    id: 'cyan_black',
    textColor: '#58F1DB',
    outlineColor: '#111111',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.7,
    cssShadowOpacity: 0.22,
    cssShadowBlur: 2,
    previewSurface: '#0A0A0A',
  },
  green_black: {
    id: 'green_black',
    textColor: '#B9E34B',
    outlineColor: '#202020',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.68,
    cssShadowOpacity: 0.2,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  cream_brown: {
    id: 'cream_brown',
    textColor: '#FFF4CF',
    outlineColor: '#6D4C41',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.2,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  peach_brown: {
    id: 'peach_brown',
    textColor: '#FFD7C8',
    outlineColor: '#8A4F3D',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.2,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  pink_black: {
    id: 'pink_black',
    textColor: '#FF77C8',
    outlineColor: '#151515',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.2,
    cssShadowBlur: 2,
    previewSurface: '#2E2E31',
  },
  black_white: {
    id: 'black_white',
    textColor: '#111111',
    outlineColor: '#FFFFFF',
    outlineScale: 0.024,
    minOutline: 1,
    shadow: 0,
    cssOutlineOpacity: 1,
    cssShadowOpacity: 0,
    cssShadowBlur: 0,
    previewSurface: '#F4F4F4',
  },
  mint_black: {
    id: 'mint_black',
    textColor: '#6BF2C6',
    outlineColor: '#111111',
    outlineScale: 0.022,
    minOutline: 1,
    shadow: 1,
    cssOutlineOpacity: 0.72,
    cssShadowOpacity: 0.2,
    cssShadowBlur: 2,
    previewSurface: '#0A0A0A',
  },
};

export const SUBTITLE_APPEARANCE_PRESET_IDS = Object.keys(PRESET_CONFIGS) as SubtitleAppearancePresetId[];

export function getSubtitleAppearancePresetId(style?: SubtitleStyle): SubtitleAppearancePresetId {
  if (style?.appearancePreset && PRESET_CONFIGS[style.appearancePreset]) {
    return style.appearancePreset;
  }

  return style?.background === false ? 'black_white' : 'white_black';
}

export function getSubtitleAppearancePreset(id: SubtitleAppearancePresetId): SubtitleAppearancePresetConfig {
  return PRESET_CONFIGS[id];
}

function hexToRgb(hex: string) {
  const normalized = hex.replace('#', '');
  const full = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const int = parseInt(full, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

export function resolveSubtitleAppearance(style: SubtitleStyle | undefined, fontSizePx: number) {
  const presetId = getSubtitleAppearancePresetId(style);
  const preset = getSubtitleAppearancePreset(presetId);
  const outlinePx = Math.max(preset.minOutline, Math.round(fontSizePx * preset.outlineScale));
  const textColor = style?.color || preset.textColor;
  const outlineColor = preset.outlineColor;
  const unitOutline = Math.max(0.5, outlinePx * 0.5);
  const outlineRgb = hexToRgb(outlineColor);

  const cssTextShadow = [
    `-${unitOutline}px -${unitOutline}px 0 rgba(${outlineRgb.r},${outlineRgb.g},${outlineRgb.b},${preset.cssOutlineOpacity})`,
    `${unitOutline}px -${unitOutline}px 0 rgba(${outlineRgb.r},${outlineRgb.g},${outlineRgb.b},${preset.cssOutlineOpacity})`,
    `-${unitOutline}px ${unitOutline}px 0 rgba(${outlineRgb.r},${outlineRgb.g},${outlineRgb.b},${preset.cssOutlineOpacity})`,
    `${unitOutline}px ${unitOutline}px 0 rgba(${outlineRgb.r},${outlineRgb.g},${outlineRgb.b},${preset.cssOutlineOpacity})`,
    ...(preset.shadow > 0
      ? [`0 1px ${preset.cssShadowBlur}px rgba(${outlineRgb.r},${outlineRgb.g},${outlineRgb.b},${preset.cssShadowOpacity})`]
      : []),
  ].join(', ');

  return {
    presetId,
    textColor,
    outlineColor,
    outlinePx,
    shadow: preset.shadow,
    cssTextShadow,
    previewSurface: preset.previewSurface,
  };
}
